import React from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

/**
 * Por qué le pedimos la cuenta bancaria justo ahora.
 *
 * Aparece cuando alguien con el plan activo va a configurar una aportación y no
 * tenemos su IBAN — típicamente porque entró por el funnel pidiendo un traspaso,
 * donde a propósito no se le pidió.
 *
 * 🔴 Los tres pasos salen SIEMPRE pendientes, y no es una simplificación: es lo
 * único que se puede afirmar. El KYC no devuelve si alguien ya entregó su IBAN,
 * su mandato o su certificado, así que la plataforma no tiene forma de saberlo.
 * Hubo una variante que lo deducía de si el usuario tenía un alta y marcaba dos
 * pasos con ✓; se quitó el 28/09/26 tras probarlo en dev: le decía «ya hecho» a
 * quien no lo tenía, y acto seguido el mini-KYC se lo volvía a pedir.
 *
 * Y sin IBAN, en la práctica, no suele haber nada: quien no ha dado la cuenta
 * tampoco ha firmado el mandato ni subido el certificado (decisión de producto
 * de Marisa, 28/09/26). Así que los tres pasos describen exactamente lo que el
 * mini-KYC va a pedir — `bank` + `docsign`, con el certificado dentro de `bank`.
 *
 * Diseño y copy (17-sep-2026, revisado 18-sep-2026): composición calcada de un
 * ejemplo de modal de transición de IBAN (cierre arriba, título, párrafo, caja
 * con los pasos en línea de tiempo, aviso de tranquilidad, un único CTA) — sin
 * icono ni bandera propios, solo el cierre de la esquina (`DialogContent` ya lo
 * trae). El comportamiento (qué dispara el CTA, que no se pueda pulsar dos
 * veces, que un fallo se vea y se pueda reintentar, y que se pueda cerrar sin
 * dar la cuenta) es el mismo de siempre — cerrar sin dar la cuenta ahora se
 * hace con esa X o con Escape, ya no con un botón "Ahora no" aparte.
 */
export interface IbanRequiredSheetProps {
	open: boolean;
	onClose: () => void;
	/** Arranca el mini-KYC: pide la URL y manda al usuario. */
	onStart: () => void;
	/** La URL se está pidiendo. */
	loading: boolean;
	/** Qué salió mal, si salió algo mal. */
	error: string | null;
	translate: (key: string) => string;
}

const IbanRequiredSheet: React.FC<IbanRequiredSheetProps> = ({
	open,
	onClose,
	onStart,
	loading,
	error,
	translate
}) => {
	/**
	 * `translate` devuelve LA PROPIA CLAVE cuando no la encuentra, no una cadena
	 * vacía — así que un `||` a secas no basta para caer al texto por defecto
	 * (regresión ya vista una vez en este mismo componente, 09/09/26).
	 */
	const text = (key: string, fallback: string) => {
		const value = translate(key);
		return !value || value === key ? fallback : value;
	};

	if (!open) return null;

	// Los 3 pasos del mini-KYC, en el orden en que los pide: el IBAN y el certificado
	// van juntos en el paso `bank` (el certificado es obligatorio ahí,
	// `isBankCertificateRequired` en el proyecto del KYC), y después el mandato SEPA,
	// que es `docsign`.
	const steps = [
		{
			key: 'individual.ibanGate.step1',
			fallback: 'Añadir tu IBAN',
			descKey: 'individual.ibanGate.step1Description',
			descFallback: 'Solo el número de cuenta, nada más.'
		},
		{
			key: 'individual.ibanGate.step2',
			fallback: 'Adjuntar el certificado de titularidad',
			descKey: 'individual.ibanGate.step2Description',
			descFallback: 'El que te da tu banco para confirmar que la cuenta es tuya.'
		},
		{
			key: 'individual.ibanGate.step3',
			fallback: 'Firmar el mandato SEPA',
			descKey: 'individual.ibanGate.step3Description',
			descFallback: 'Autorizas el cargo de tus aportaciones a esa cuenta.'
		}
	];

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent
				data-testid="iban-required-sheet"
				// `max-h`/`overflow-y-auto` como los demás diálogos de la casa (18 los llevan, entre
				// ellos `OtherPlansSheet` y `StartProcessSheet`). Sin esto el contenido no cabe en un
				// móvil pequeño: `DialogContent` es `fixed` y centrado, así que lo que sobra se recorta
				// por ARRIBA y por abajo sin barra de scroll — la X se va fuera por arriba y el CTA por
				// abajo, y en táctil no hay Escape. La única salida sería recargar la página. Pasa
				// también en escritorio con el zoom al 200 % (WCAG 1.4.4).
				className="individual-v2 sm:max-w-md p-5 sm:p-6 max-h-[90dvh] overflow-y-auto"
			>
				<DialogHeader>
					<DialogTitle className="font-heading text-2xl">
						{text('individual.ibanGate.title', 'Necesitamos tu IBAN')}
					</DialogTitle>
					<DialogDescription className="pt-2">
						{text(
							'individual.ibanGate.body',
							'Para hacer tu aportación al plan necesitamos una cuenta bancaria a tu nombre. Es rápido y seguro.'
						)}
					</DialogDescription>
				</DialogHeader>

				<p className="text-sm font-medium text-foreground">
					{text('individual.ibanGate.stepsIntro', 'Solo tienes que:')}
				</p>

				{/* `<ol>`/`<li>`, no divs: aquí el orden significa algo, y así la tecnología asistiva
				    anuncia «lista de 3 elementos» y las fronteras entre pasos. `list-none` porque la
				    numeración la pinta el círculo. */}
				<ol className="list-none rounded-2xl border border-border bg-muted/40 p-4">
					{steps.map((step, idx) => (
						<li key={step.key}>
							<div data-testid={`iban-required-step-${idx + 1}`} className="flex items-start gap-3">
								<span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary-text">
									{idx + 1}
								</span>
								<div>
									<p className="text-sm font-semibold text-foreground">
										{text(step.key, step.fallback)}
									</p>
									<p className="text-xs text-muted-foreground mt-0.5">
										{text(step.descKey, step.descFallback)}
									</p>
								</div>
							</div>
							{idx < steps.length - 1 && (
								<div
									aria-hidden="true"
									className="ml-3 h-4 w-0.5 border-l-2 border-dashed border-muted-foreground/30"
								/>
							)}
						</li>
					))}
				</ol>

				<p className="text-sm text-muted-foreground">
					{text(
						'individual.ibanGate.closing',
						'Y listo. Volverás aquí automáticamente para continuar.'
					)}
				</p>

				<p className="flex items-start gap-2 rounded-xl bg-muted/40 p-3 text-sm text-muted-foreground">
					<span aria-hidden="true">💡</span>
					{text(
						'individual.ibanGate.reassurance',
						'Tu plan y tu traspaso no cambian. Solo necesitamos la cuenta para hacer la aportación.'
					)}
				</p>

				{error && (
					// `role="alert"` para que se anuncie: el CTA se vuelve a habilitar al fallar, así que sin
					// esto alguien que no ve la pantalla pulsa otra vez sin saber que salió un error
					// (WCAG 4.1.3).
					<p role="alert" data-testid="iban-required-error" className="text-sm text-destructive">
						{error}
					</p>
				)}

				<Button
					data-testid="iban-required-cta"
					className="w-full"
					onClick={onStart}
					disabled={loading}
				>
					{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
					{text('individual.ibanGate.cta', 'Añadir mi IBAN')}
				</Button>
			</DialogContent>
		</Dialog>
	);
};

export default IbanRequiredSheet;
